import { useEffect, useState } from 'react';

import { ScoreType } from '../../../store/admin/eventFormats/store';
import {
  IAdminTeamHoleResult,
  IAdminTeamHoleResultsStore
} from '../../../store/admin/teamHoleResults/store';
import { IAdminTeamResult, IAdminTeamResultsStore } from '../../../store/admin/teamResults/store';
import { getHoleScoreMock } from '../../../store/player/scores/mocks';
import { IHoleScore } from '../../../store/player/scores/store';
import { TIFormatPrefix } from '../../../store/admin/roundFormats/store';

import { capitalizeFirstLetter, groupBy, lowercaseFirstLetter } from '../../../contrib/utils';
import { IObjectById } from '../../../contrib/Utils/getObjectByIdFromArray';
import { TTeamFormatPrefix } from '../../admin/EventWizard/Common/AssignResults/Context/AssignResults.context';
import { sortResultsWithTiebreak } from '../../../contrib/sortResultsWithTiebreak';

export interface ITeamHoleScore {
  handicap: number;
  scores: IHoleScore[];
}

interface ITeamsHoleResultsByGroupId {
  [groupId: number]: IAdminTeamHoleResult[];
}

interface IUseLeaderboardsResultsArgs {
  adminTeamResults: IAdminTeamResultsStore;
  roundId: number;
  selectedFlightId?: number;
  selectedScoreType?: ScoreType;
  selectedTeamFormat?: TTeamFormatPrefix | TIFormatPrefix;
  adminTeamHoleResults: IAdminTeamHoleResultsStore;
  formatIsStableford: boolean;
}
type TUseLeaderboardsResults = (
  args: IUseLeaderboardsResultsArgs
) => [IAdminTeamResult[], IObjectById<ITeamHoleScore>];

export const useLeaderboardsResults: TUseLeaderboardsResults = args => {
  const {
    adminTeamResults,
    roundId,
    selectedFlightId,
    selectedScoreType,
    selectedTeamFormat,
    adminTeamHoleResults,
    formatIsStableford
  } = args;

  const [sortedResults, setSortedResults] = useState<IAdminTeamResult[]>([]);
  const [
    teamsHoleResultsByGroupId,
    setTeamsHoleResultsByGroupId
  ] = useState<ITeamsHoleResultsByGroupId | null>(null);
  const [formattedTeamsHoleResults, setFormattedTeamsHoleResults] = useState<
    IObjectById<ITeamHoleScore>
  >({});

  const getHoleField = (field: string) => {
    if (selectedTeamFormat && selectedScoreType) {
      const holeTeamFormat = lowercaseFirstLetter(selectedTeamFormat.replace('teamStroke', ''));
      const formattedScoreType = capitalizeFirstLetter(ScoreType[selectedScoreType].toLowerCase());

      return `${holeTeamFormat}${formattedScoreType}${capitalizeFirstLetter(field)}`;
    }
    return '';
  };

  const getHoleScore = (holeResult: IAdminTeamHoleResult) => {
    return getHoleScoreMock({
      gross: holeResult[getHoleField('Score') as keyof IAdminTeamHoleResult] as any,
      holeHandicap: holeResult[getHoleField('Handicap') as keyof IAdminTeamHoleResult] as any,
      holePar: holeResult[getHoleField('Par') as keyof IAdminTeamHoleResult] as any,
      net: holeResult[getHoleField('Score') as keyof IAdminTeamHoleResult] as any,
      order: holeResult.hole
    });
  };

  useEffect(() => {
    if (
      !!adminTeamResults &&
      roundId &&
      selectedFlightId &&
      selectedScoreType &&
      adminTeamResults[roundId] &&
      adminTeamResults[roundId][selectedFlightId]
    ) {
      const scoreType = capitalizeFirstLetter(ScoreType[selectedScoreType].toLowerCase());
      const teamFormatScoreType = `${selectedTeamFormat}${scoreType}`;
      const positionField = `${teamFormatScoreType}Position`;

      const results = adminTeamResults[roundId][selectedFlightId];

      const newSortedResults = sortResultsWithTiebreak(
        results,
        selectedScoreType,
        formatIsStableford,
        selectedTeamFormat,
        positionField
      );
      // const sortedResults =sortBy(results, 'asc', positionField);

      setSortedResults(newSortedResults);
    }
  }, [
    adminTeamResults,
    roundId,
    selectedFlightId,
    selectedTeamFormat,
    selectedScoreType,
    formatIsStableford
  ]);

  useEffect(() => {
    if (
      roundId &&
      adminTeamHoleResults &&
      selectedFlightId &&
      adminTeamHoleResults[roundId] &&
      adminTeamHoleResults[roundId][selectedFlightId]
    ) {
      const currentFlightTeamHoleResults = adminTeamHoleResults[roundId][selectedFlightId];

      setTeamsHoleResultsByGroupId(groupBy(currentFlightTeamHoleResults, 'teamGameId'));
    }
  }, [adminTeamHoleResults, roundId, selectedFlightId, selectedTeamFormat]);

  useEffect(() => {
    if (!!teamsHoleResultsByGroupId) {
      const formattedResults = Object.values(teamsHoleResultsByGroupId).reduce(
        (acc: any, holeResults: IAdminTeamHoleResult[]) => {
          const score: ITeamHoleScore = {
            handicap: -1,
            scores: holeResults.map(getHoleScore)
          };

          return { ...acc, [holeResults[0].teamGameId]: score };
        },
        {}
      );

      setFormattedTeamsHoleResults(formattedResults);
    }
  }, [teamsHoleResultsByGroupId]);

  return [sortedResults, formattedTeamsHoleResults];
};
